import { Component, ElementRef, ViewChild, OnDestroy, OnInit, NgZone, ChangeDetectorRef } from '@angular/core';
import * as XLSX from 'xlsx';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CostReportService } from './../../services/cost-upload-report.service';
import { validateWorkbookAllColumns, ValidatorConfig } from '../../services/excel-validator-gm';
import { FocusValidatorConfig, validateFocusAreaWorkbook } from '../../services/excel-validator-focus';
import { FileUploadService } from '../../services/file-upload-report.service';

type Cell = string | number | boolean | null;

interface ValidationIssue {
  sheet?: string;
  row?: number;
  col?: string | number;
  message: string;
}

interface ValidationResult {
  errors: ValidationIssue[];
  issueTable: Cell[][];
}

interface MissingUser {
  payroll_ctempid?: string;
  payroll_empname?: string;
  payroll_loc?: string;
  payroll_currency?: string;
  ErrorAtInsert?: string;
  ImportStatus?: number;
}

const gmCfg: ValidatorConfig = {
  sheetSelector: 'auto',
  ignoreEmptySheets: true,
  deleteEmptyRows: true,
  returnCleanedWorkbook: false,
  stopAtTotalRow: false,
  clientsLabel: 'Clients',
  totalLabel: 'Total'
};

const focusCfg: FocusValidatorConfig = {
  sheetSelector: 'auto',
  ignoreEmptySheets: true,
  deleteEmptyRows: true,
  returnCleanedWorkbook: false,
  headerMustContain: ['FC Client', 'Emp ID'],
  skipHeaders: []
};

@Component({
  selector: 'MarginCostUpload-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  standalone: false
})
export class MarginCostUploadViewComponent implements OnInit, OnDestroy {
  // ---------- Tab 2: Upload Cost Data ----------
  file: File | null = null;
  previewRows: any[] = [];
  isUploading = false;
  fetchReportDataSubscription?: Subscription;
  lastUpdatedOnCost = '';

  @ViewChild('nativeCostFileInput') nativeCostFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('uploadPassInput') uploadPassInput?: ElementRef<HTMLInputElement>;
  showUploadPass = false;

  successMessage = '';
  errorMessage = '';
  private _successTimer: any = null;

  missingUsers: MissingUser[] = [];
  showCurrentPass = false;
  showNewPass = false;
  showConfirmNewPass = false;

  isFileStructValid = true;
  structureIssues: string[] = [];

  // ---------- TAB 3: Change Passphrase ----------
  isSavingPass = false;
  passChangeError = '';
  passChangeSuccess = '';
  lastUpdatedOnChangePass = '';
  updatePassSubscription?: Subscription;

  @ViewChild('currentPassInput') currentPassInput?: ElementRef<HTMLInputElement>;
  @ViewChild('newPassInput') newPassInput?: ElementRef<HTMLInputElement>;
  @ViewChild('confirmNewPassInput') confirmNewPassInput?: ElementRef<HTMLInputElement>;

  // ---------- TAB 1: Margin Upload ----------
  // (legacy margin path kept if referenced elsewhere)
  marginFile: File | null = null;
  marginPreviewRows: any[] = [];
  lastUpdatedOnMargin = '';
  lastUpdatedOnFocus = '';

  // Margin messages + flags
  marginSuccessMessage = '';
  marginErrorMessage = '';
  private _marginSuccessTimer: any = null;

  @ViewChild('marginPassInput') marginPassInput?: ElementRef<HTMLInputElement>;
  showMarginUploadPass = false;

  isUploadingMargin = false;
  isMarginFileStructValid = true;
  marginStructureIssues: string[] = [];

  @ViewChild('excelFile') fileInput?: ElementRef<HTMLInputElement>;

  // Validator-based flow (Margin)
  selectedFile: File | null = null;
  selectedFileName = '';
  selectedFileSize: number | null = null;

  parsedData: Cell[][] = [];

  validationTable: Cell[][] = [];
  validationIssues: ValidationIssue[] = [];
  validationRows: Record<string, Cell>[] = [];
  validationColumns: string[] = [];

  uploaded = false;
  uploadProgress = 0;

  isValidating = false;  // 🔑 drives loader on Margin tab
  isFileValid = false;
  validationErrorCount = 0;
  validationSummary = '';

  private _uploadTimer: any;
  private readonly MAX_FILE_SIZE = 20 * 1024 * 1024;

  constructor(
    private costReportService: CostReportService,
    private uploadApi: FileUploadService,
    private ngZone: NgZone,               // 🔑 ensure UI updates
    private cdr: ChangeDetectorRef        // 🔑 force change detection if needed
  ) {}

  ngOnInit(): void {
    this.fetchLastUpdatedTimestampMargin();
    this.fetchLastUpdatedTimestampCost();
    this.fetchLastUpdatedTimestampChangePass();
  }

  ngOnDestroy(): void {
    if (this.fetchReportDataSubscription) this.fetchReportDataSubscription.unsubscribe();
    if (this.updatePassSubscription) this.updatePassSubscription.unsubscribe();

    if (this._successTimer) { clearTimeout(this._successTimer); this._successTimer = null; }
    if (this._marginSuccessTimer) { clearTimeout(this._marginSuccessTimer); this._marginSuccessTimer = null; }
    if (this._uploadTimer) clearInterval(this._uploadTimer);
  }

  // ===== Derived display to avoid number pipe =====
  get selectedFileSizeFormatted(): string {
    if (this.selectedFileSize == null) return '';
    try {
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })
        .format(this.selectedFileSize);
    } catch { return String(this.selectedFileSize); }
  }

  // ============================================
  // ========== COST / PAYROLL (TAB 2) ==========
  // ============================================

  openNativeFileDialog() {
    if (this.isUploading || this.isSavingPass) return;
    this.nativeCostFileInput?.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const picked = input?.files?.[0] ?? null;
    this.file = picked;

    this.errorMessage = '';
    this.successMessage = '';
    this.missingUsers = [];

    this.isFileStructValid = true;
    this.structureIssues = [];
    this.isUploading = true;
    if (this.file) {
      this.previewRows = [];
      this.readExcel();
    } else {
      this.previewRows = [];
    }
    this.isUploading = false;
  }

  readExcel() {
    if (!this.file) return;
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      // 🔑 Wrap in zone to update UI
      this.ngZone.run(() => {
        const data = e.target?.result;
        if (!data) return;

        const workbook = XLSX.read(data as string, { type: 'binary' });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet] as XLSX.WorkSheet;
        const json = XLSX.utils.sheet_to_json(sheet);
        this.previewRows = json;

        const struct = validatePayrollStructureFromSheetAsJson(sheet, {
          requiredHeaders: REQUIRED_HEADERS,
          sheetName: firstSheet
        });
        this.isFileStructValid = struct.ok;
        this.structureIssues = struct.issues;

        if (!struct.ok) {
          this.errorMessage = 'The selected file is not in the expected format. Please use the provided template.';
        } else {
          if (!this.missingUsers.length) this.errorMessage = '';
        }
        this.cdr.markForCheck();
      });
    };
    reader.readAsBinaryString(this.file);
  }

  uploadToServer() {
    this.errorMessage = '';
    this.successMessage = '';
    this.missingUsers = [];

    const pass = this.uploadPassInput?.nativeElement.value.trim() || '';

    if (!this.file) { this.errorMessage = 'Please select a file first.'; return; }
    if (!pass) { this.errorMessage = 'Please enter the passphrase.'; return; }
    if (!this.file.name?.toLowerCase().endsWith('.xlsx')) {
      this.errorMessage = 'Unsupported file type. Please upload an .xlsx file only.'; return;
    }
    if (!this.isFileStructValid) {
      this.errorMessage = 'Uploaded file is not in the correct format. Please download and use the latest template.'; return;
    }

    this.isUploading = true;
    this.fetchReportDataSubscription = this.costReportService
      .insertCostReportData(pass, this.previewRows)
      .pipe(finalize(() => this.ngZone.run(() => { this.isUploading = false; this.cdr.markForCheck(); })))
      .subscribe({
        next: (res: any) => this.ngZone.run(() => {
          if (res !== null && res.Message.recordset[0]?.Message !== 'Success') {
            const rows = Array.isArray(res.Message.recordset) ? res.Message.recordset : [];
            this.missingUsers = rows.filter((r: any) => r.ImportStatus === 2);

            if (this.missingUsers.length > 0) {
              this.errorMessage = `${this.missingUsers.length} row(s) contain invalid employee details. Please review the table below.`;
              this.resetUploadForm();
            } else {
              this.errorMessage = res.Message.recordset[0].Message;
            }
          } else {
            this.resetUploadForm();
            this.showSuccess('Upload completed successfully.', 8000);
            this.fetchLastUpdatedTimestampCost();
          }
          this.cdr.markForCheck();
        }),
        error: (err) => this.ngZone.run(() => {
          console.error(err);

          const rows = Array.isArray(err?.error) ? err.error : [];
          this.missingUsers = rows.filter((r: any) => r.ImportStatus === 2);

          if (this.missingUsers.length) {
            this.errorMessage = `${this.missingUsers.length} row(s) failed during upload. See details below.`;
          } else {
            this.errorMessage = 'Upload failed. Please try again.';
          }
          this.resetUploadForm();
          this.cdr.markForCheck();
        })
      });
  }

  private resetUploadForm() {
    this.file = null;
    this.previewRows = [];
    this.showUploadPass = false;

    this.isFileStructValid = true;
    this.structureIssues = [];

    if (this.uploadPassInput?.nativeElement) this.uploadPassInput.nativeElement.value = '';
    if (this.nativeCostFileInput?.nativeElement) this.nativeCostFileInput.nativeElement.value = '';
  }

  onClearClick() {
    this.errorMessage = '';
    this.successMessage = '';
    this.missingUsers = [];
    this.resetUploadForm();
  }

  private showSuccess(message: string, ms = 8000) {
    this.successMessage = message;
    if (this._successTimer) { clearTimeout(this._successTimer); this._successTimer = null; }
    this._successTimer = setTimeout(() => {
      this.ngZone.run(() => { this.successMessage = ''; this._successTimer = null; this.cdr.markForCheck(); });
    }, ms);
  }

  // ============================================
  // ========== CHANGE PASSPHRASE (TAB 3) =======
  // ============================================

  canSaveNewPassphrase(): boolean {
    const cur = (this.currentPassInput?.nativeElement.value || '').trim();
    const next = (this.newPassInput?.nativeElement.value || '').trim();
    const confirm = (this.confirmNewPassInput?.nativeElement.value || '').trim();

    return (!!cur && !!next && !!confirm && next === confirm && next.length >= 4 && next !== cur);
  }

  saveNewPassphrase() {
    this.passChangeError = '';
    this.passChangeSuccess = '';

    if (!this.canSaveNewPassphrase()) {
      this.passChangeError =
        'Please check the inputs: all fields are required, new must match confirm, be at least 4 characters, and differ from current.';
      return;
    }

    const cur = (this.currentPassInput?.nativeElement.value || '').trim();
    const next = (this.newPassInput?.nativeElement.value || '').trim();

    this.isSavingPass = true;

    this.updatePassSubscription = this.costReportService
      .updatePassphrase(cur, next)
      .pipe(finalize(() => this.ngZone.run(() => { this.isSavingPass = false; this.cdr.markForCheck(); })))
      .subscribe({
        next: (res: any) => this.ngZone.run(() => {
          if (res.recordset?.[0]?.Message === 'Success') {
            if (this.currentPassInput?.nativeElement) this.currentPassInput.nativeElement.value = '';
            if (this.newPassInput?.nativeElement) this.newPassInput.nativeElement.value = '';
            if (this.confirmNewPassInput?.nativeElement) this.confirmNewPassInput.nativeElement.value = '';
            this.showCurrentPass = this.showNewPass = this.showConfirmNewPass = false;
            this.showCostUploadSuccess('Passphrase updated successfully.');
            this.fetchLastUpdatedTimestampChangePass();
          } else {
            this.passChangeError = res.recordset?.[0]?.Message || 'Failed to update passphrase.';
          }
          this.cdr.markForCheck();
        }),
        error: (err) => this.ngZone.run(() => {
          console.error(err);
          this.passChangeError = 'Failed to update passphrase. Please try again.';
          this.cdr.markForCheck();
        })
      });
  }

  private showCostUploadSuccess(message: string, ms = 8000) {
    this.passChangeSuccess = message;
    if (this._successTimer) { clearTimeout(this._successTimer); this._successTimer = null; }
    this._successTimer = setTimeout(() => {
      this.ngZone.run(() => { this.passChangeSuccess = ''; this._successTimer = null; this.cdr.markForCheck(); });
    }, ms);
  }

  toggle(field: 'upload' | 'current' | 'new' | 'confirm' | 'marginUpload') {
    switch (field) {
      case 'upload':  this.showUploadPass = !this.showUploadPass; break;
      case 'current': this.showCurrentPass = !this.showCurrentPass; break;
      case 'new':     this.showNewPass = !this.showNewPass; break;
      case 'confirm': this.showConfirmNewPass = !this.showConfirmNewPass; break;
      case 'marginUpload': this.showMarginUploadPass = !this.showMarginUploadPass; break;
    }
  }

  // ============================================
  // ========== MARGIN UPLOAD (TAB 1) ===========
  // ============================================

  openNativeMarginFileDialog() {
    if (this.isUploadingMargin || this.isSavingPass) return;
    this.fileInput?.nativeElement.click();
  }

  onMarginFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const picked = input?.files?.[0] ?? null;
    this.marginFile = picked;

    this.clearMarginMessages();

    this.isMarginFileStructValid = true;
    this.marginStructureIssues = [];

    if (this.marginFile) {
      this.marginPreviewRows = [];
      this.readMarginExcel();
    } else {
      this.marginPreviewRows = [];
    }
  }

  readMarginExcel() {
    if (!this.marginFile) return;

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.ngZone.run(() => {
        const data = e.target?.result;
        if (!data) return;

        const workbook = XLSX.read(data as string, { type: 'binary' });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet] as XLSX.WorkSheet;
        const json = XLSX.utils.sheet_to_json(sheet);
        this.marginPreviewRows = json;

        const struct = validateMarginStructure(sheet, { sheetName: firstSheet });
        this.isMarginFileStructValid = struct.ok;
        this.marginStructureIssues = struct.issues;

        if (!struct.ok) {
          this.marginErrorMessage = 'Selected margin file is not in the expected format. Please use the latest template.';
        } else {
          this.marginErrorMessage = '';
        }
        this.cdr.markForCheck();
      });
    };

    reader.readAsBinaryString(this.marginFile);
  }

  // ===== New margin flow using validators and FileUploadService =====
  onFileChange(evt: Event) {
    this.clearMarginMessages();

    this.validationTable = [];
    this.validationIssues = [];
    this.validationRows = [];
    this.validationColumns = [];
    this.validationSummary = '';
    this.isFileValid = false;
    this.validationErrorCount = 0;

    const input = evt.target as HTMLInputElement;
    if (!input?.files?.length) { this.clearSelection(); return; }

    if (input.files.length > 1) { this.marginErrorMessage = 'Please select only one file at a time.'; return; }

    const file = input.files[0];

    if (!(file instanceof Blob)) { this.marginErrorMessage = 'Invalid file object selected. Please re-select the Excel file.'; return; }
    if (!file.name?.toLowerCase().endsWith('.xlsx')) { this.marginErrorMessage = 'Unsupported file type. Please upload an .xlsx file only.'; return; }
    if (file.size === 0) { this.marginErrorMessage = 'The file is empty.'; return; }

    this.selectedFile = file;
    this.selectedFileName = file.name;
    this.selectedFileSize = file.size;
    this.uploaded = false;

    const reader = new FileReader();

    reader.onerror = () => {
      this.ngZone.run(() => {
        this.marginErrorMessage = 'Failed to read file. Please try again.';
        this.isFileValid = false;
        this.cdr.markForCheck();
      });
    };

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      // Start loader inside the zone
      this.ngZone.run(() => { this.isValidating = true; this.cdr.markForCheck(); });

      const arrayBuffer = e.target?.result as ArrayBuffer | null;
      if (!arrayBuffer) {
        this.ngZone.run(() => {
          this.marginErrorMessage = 'Could not read file content.';
          this.isFileValid = false;
          this.isValidating = false;
          this.cdr.markForCheck();
        });
        return;
      }

      try {
        // Parse preview (still outside zone, we update inside)
        const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true, raw: false });
        const previewSheet = wb.SheetNames.includes('HHP 1') ? 'HHP 1' : wb.SheetNames[0];
        const ws = wb.Sheets[previewSheet];

        if (ws) {
          const all = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, blankrows: true }) as Cell[][];
          const PREVIEW_ROWS = 50;
          this.ngZone.run(() => { this.parsedData = all.slice(0, PREVIEW_ROWS); this.cdr.markForCheck(); });
        } else {
          this.ngZone.run(() => { this.parsedData = []; this.cdr.markForCheck(); });
        }

        // Validate workbook (async)
        let validationResult: any = null;
        if (file.name.toLowerCase().includes('gm')) {
          validationResult = await validateWorkbookAllColumns(file, gmCfg);
        } else {
          validationResult = await validateFocusAreaWorkbook(file, focusCfg);
        }
        validationResult = validationResult ?? { errors: [], issueTable: [] };

        // 🔑 Apply results inside Angular zone
        this.ngZone.run(() => {
          this.validationTable = Array.isArray(validationResult.issueTable) ? validationResult.issueTable : [];
          this.validationIssues = Array.isArray(validationResult.errors) ? validationResult.errors : [];
          this.validationErrorCount = this.validationIssues.length;

          this.validationColumns = this.validationTable.length ? (this.validationTable[0] as string[]) : [];
          this.validationRows = this.validationTable.length
            ? this.validationTable.slice(1).map((row: Cell[]) => {
                const obj: Record<string, Cell> = {};
                this.validationColumns.forEach((h, i) => (obj[h] = row?.[i] ?? null));
                return obj;
              })
            : [];

          if (this.validationIssues.length > 0) {
            this.validationSummary = `Excel validation failed. Found ${this.validationIssues.length} issue(s).`;
            this.marginErrorMessage = '';   // rely on the summary + table
            this.isFileValid = false;
          } else {
            this.validationSummary = '';
            this.marginSuccessMessage = 'Validation passed. Ready to upload.';
            this.isFileValid = true;
          }
          this.cdr.markForCheck();
        });
      } catch (err: any) {
        this.ngZone.run(() => {
          this.isFileValid = false;
          this.marginErrorMessage = err?.message || 'Failed to parse/validate workbook.';
          this.cdr.markForCheck();
        });
      } finally {
        // Stop loader inside zone
        this.ngZone.run(() => { this.isValidating = false; this.cdr.markForCheck(); });
      }
    };

    reader.readAsArrayBuffer(file);
  }

  uploadExcel() {
    this.clearMarginMessages();

    if (!this.canUpload) { this.marginErrorMessage = 'Please fix validation errors before uploading.'; return; }
    if (!this.selectedFile) { this.marginErrorMessage = 'No file selected.'; return; }

    this.ngZone.run(() => { this.isUploadingMargin = true; this.cdr.markForCheck(); });

    this.uploadApi.upload(this.selectedFile).subscribe({
      next: (res: any) => this.ngZone.run(() => {
        this.uploaded = true;
        this.isUploadingMargin = false;

        const msg = 'Upload completed successfully.';
        this.clearSelection();                 // clears selection + validation data (keeps messages)
        this.showMarginSuccess(msg, 8000);     // show success
        this.fetchLastUpdatedTimestampMargin();  // refresh timestamp
        this.cdr.markForCheck();
      }),
      error: (err) => this.ngZone.run(() => {
        this.marginErrorMessage = err?.error?.message || err?.message || 'Upload failed. Please try again.';
        this.isUploadingMargin = false;
        this.cdr.markForCheck();
      })
    });
  }

  clearSelection() {
    this.selectedFile = null;
    this.selectedFileName = '';
    this.selectedFileSize = null;

    this.parsedData = [];
    this.validationTable = [];
    this.validationIssues = [];
    this.validationRows = [];
    this.validationColumns = [];
    this.validationSummary = '';

    this.isValidating = false;
    this.isFileValid = false;
    this.validationErrorCount = 0;

    this.isUploadingMargin = false;
    this.uploaded = false;
    this.uploadProgress = 0;

    // Clear messages — NOTE: we restore successMessage after calling this from uploadExcel()
    this.clearMarginMessages();

    // ✅ Reset the actual <input type="file">
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private clearMarginMessages() {
    this.marginErrorMessage = '';
    this.marginSuccessMessage = '';
    this.validationSummary = '';
  }

  private showMarginSuccess(message: string, ms = 6000) {
    this.marginSuccessMessage = message;
    if (this._marginSuccessTimer) { clearTimeout(this._marginSuccessTimer); this._marginSuccessTimer = null; }
    this._marginSuccessTimer = setTimeout(() => {
      this.ngZone.run(() => { this.marginSuccessMessage = ''; this._marginSuccessTimer = null; this.cdr.markForCheck(); });
    }, ms);
  }

  get canUpload(): boolean {
    return !!this.selectedFile && !this.isValidating && !this.isUploadingMargin && this.isFileValid;
  }

  private fetchLastUpdatedTimestampMargin(): void {
    this.costReportService.getLastUpdatedOn('margin-upload').subscribe({
      next: (response: any) => {
        if (response[0][0]?.lastUpdatedOnForGM && response[1][0]?.lastUpdatedOnForFocus) {
          this.lastUpdatedOnMargin = new Date(response[0][0].lastUpdatedOnForGM).toLocaleString();
          this.lastUpdatedOnFocus = new Date(response[1][0].lastUpdatedOnForFocus).toLocaleString();
        }
      },
      error: () => {
        this.lastUpdatedOnMargin = 'N/A';
        this.lastUpdatedOnFocus = 'N/A';
      }
    });
  }

  private fetchLastUpdatedTimestampCost(): void {
    this.costReportService.getLastUpdatedOn('cost-data').subscribe({
      next: (response: any) => {
        if (response[0][0]?.lastUpdatedOn) {
          this.lastUpdatedOnCost = new Date(response[0][0].lastUpdatedOn).toLocaleString();
        }
      },
      error: () => {
        this.lastUpdatedOnCost = 'N/A';
      }
    });
  }

  private fetchLastUpdatedTimestampChangePass(): void {
    this.costReportService.getLastUpdatedOn('change-passphrase').subscribe({
      next: (response: any) => {
        if (response[0][0]?.lastUpdatedOn) {
          this.lastUpdatedOnChangePass = new Date(response[0][0].lastUpdatedOn).toLocaleString();
        }
      },
      error: () => {
        this.lastUpdatedOnChangePass = 'N/A';
      }
    });
  }
}

/* -----------------------------------------------------------
   Structure validators (pure helpers; additive only)
----------------------------------------------------------- */

type StructCheckOptions = {
  requiredHeaders?: string[];
  sheetName?: string;
};

const REQUIRED_HEADERS = [
  'Date',
  'Emp ID',
  'Currency',
  'Amount',
];

const CURRENCY_ALLOWLIST = new Set(['INR', 'USD', 'EUR', 'GBP', 'AED']);

function normalizeHeader(v: any): string {
  return String(v ?? '').trim();
}

function isExcelDate(value: any): boolean {
  if (value instanceof Date && !isNaN(value.getTime())) return true;
  if (typeof value === 'number') return value > 0; // Excel serial
  if (typeof value === 'string') { const d = new Date(value); return !isNaN(d.getTime()); }
  return false;
}

function validatePayrollStructureFromSheetAsJson(
  sheet: XLSX.WorkSheet,
  opts: StructCheckOptions
): { ok: boolean; issues: string[] } {
  const issues: string[] = [];

  const rows = XLSX.utils.sheet_to_json<any[]>(sheet, {
    header: 1, defval: null, raw: false, blankrows: false
  });

  if (!rows.length) { issues.push('Sheet is empty.'); return { ok: false, issues }; }

  const header = (rows[0] ?? []).map(normalizeHeader);

  const required = opts.requiredHeaders || [];
  const missing = required.filter(h => !header.includes(h));
  if (missing.length) { issues.push(`Missing required column(s): ${missing.join(', ')}`); return { ok: false, issues }; }

  const idx: Record<string, number> = {};
  header.forEach((h, i) => (idx[h] = i));

  const DATA_PROBE = rows.slice(1, 1 + 25);
  for (let r = 0; r < DATA_PROBE.length; r++) {
    const row = DATA_PROBE[r] || [];
    const rowNo = r + 2;

    const get = (col: string) => row[idx[col]];
    const asString = (v: any) => (v === null || v === undefined ? '' : String(v).trim());

    if (!asString(get('Emp ID'))) issues.push(`Row ${rowNo}: Emp ID is required.`);

    const dt = get('Date');
    if (dt !== null && dt !== undefined && !isExcelDate(dt)) issues.push(`Row ${rowNo}: Date is not a valid date/Excel serial.`);

    const amtRaw = get('Amount');
    if (amtRaw !== null && amtRaw !== undefined) {
      const amt = typeof amtRaw === 'number' ? amtRaw : Number(String(amtRaw).replace(/,/g, ''));
      if (!isFinite(amt)) issues.push(`Row ${rowNo}: Amount must be numeric.`);
    }

    const ccy = asString(get('Currency'));
    if (ccy && !CURRENCY_ALLOWLIST.has(ccy)) issues.push(`Row ${rowNo}: Currency "${ccy}" is not allowed.`);
  }

  return { ok: issues.length === 0, issues };
}

function validateMarginStructure(
  sheet: XLSX.WorkSheet,
  _opts?: StructCheckOptions
): { ok: boolean; issues: string[] } {
  const issues: string[] = [];
  const rows = XLSX.utils.sheet_to_json<any[]>(sheet, {
    header: 1, defval: null, raw: false, blankrows: false
  });

  if (!rows.length) { issues.push('Sheet is empty.'); return { ok: false, issues }; }
  if (rows.length < 2) { issues.push('No data rows found (only header present).'); return { ok: false, issues }; }

  return { ok: issues.length === 0, issues };
}